import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, PenLine, Library, FileText, Loader2, Pencil, Trash2, X, Clock, User, Globe, SlidersHorizontal, Volume2, Type, Wand2, Moon, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Btn, Input, Badge, TabBar } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import Documents from "@/pages/Documents";
import { VIS_LABEL } from "@/pages/Documents";

const KN_STATUS = { current: "Atual", archived: "Arquivado", superseded: "Substituído", expired: "Expirado", pending: "Pendente", draft: "Rascunho" };

const CHANNEL_LABEL = {
  whatsapp: "via WhatsApp",
  console: "no console",
  pending: "via pendência respondida",
  isa: "via ISA",
};

const SCOPE_LABEL = (s) => {
  if (!s) return "—";
  if (s === "company") return "Empresa";
  if (s === "team") return "Equipe";
  if (s.startsWith("department:")) return `Setor · ${s.split(":")[1]}`;
  if (s.startsWith("account:")) return "Conta";
  if (s.startsWith("employee:")) return "Pessoal";
  if (s.startsWith("project:")) return "Projeto";
  return s;
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

/* -------------------- edit knowledge modal -------------------- */
function EditNodeModal({ node, onClose, onSaved }) {
  const [f, setF] = useState({ title: node.title || "", content: node.content || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/knowledge/${node.id}`, f);
      toast.success("Conhecimento atualizado");
      onSaved();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={onClose} data-testid="edit-knode-modal">
      <Card className="w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Editar conhecimento</h3>
          <button onClick={onClose} className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-slate-600">Título</label>
            <Input data-testid="edit-knode-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-slate-600">Conteúdo</label>
            <textarea data-testid="edit-knode-content" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} required
              className="min-h-[160px] w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 transition-[border-color,box-shadow] duration-200 focus:border-[#0b1220]/40 focus:outline-none focus:ring-4 focus:ring-[#0b1220]/5" />
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Btn type="button" variant="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn data-testid="edit-knode-save" disabled={busy}>{busy ? "Salvando…" : "Salvar alterações"}</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Alimentar({ onSaved }) {
  const [f, setF] = useState({ title: "", content: "", scope: "company" });
  const [busy, setBusy] = useState(false);
  const [visibility, setVisibility] = useState("department");
  const [urlF, setUrlF] = useState({ url: "", title: "", scope: "company" });
  const [urlBusy, setUrlBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/knowledge", { ...f, department: "commercial" });
      toast.success("Adicionado ao cérebro");
      setF({ title: "", content: "", scope: "company" });
      onSaved?.();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const addUrl = async (e) => {
    e.preventDefault();
    setUrlBusy(true);
    try {
      const { data } = await api.post("/knowledge/url", { ...urlF, department: "commercial" });
      toast.success(`Site lido e salvo no cérebro: ${data.title}`);
      setUrlF({ url: "", title: "", scope: "company" });
      onSaved?.();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUrlBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("visibility_scope", visibility);
    try {
      const { data } = await api.post("/documents/upload", fd);
      toast.success(data.duplicate ? "Documento já existia (duplicado)" : "Documento enviado — processando em segundo plano");
      onSaved?.();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2"><PenLine size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-zinc-900">Escrever conhecimento</h2></div>
        <form onSubmit={add} className="space-y-3">
          <Input data-testid="knowledge-title" placeholder="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          <select data-testid="knowledge-scope" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}
            className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm">
            <option value="company">Empresa</option>
            <option value="department:commercial">Departamento (Comercial)</option>
            <option value="team">Equipe</option>
          </select>
          <textarea data-testid="knowledge-content" placeholder="Conteúdo" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} required
            className="min-h-[120px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/15" />
          <Btn data-testid="knowledge-submit" className="w-full"><Plus size={16} /> Adicionar ao cérebro</Btn>
        </form>
      </Card>

      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2"><Upload size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-zinc-900">Enviar documento</h2></div>
        <p className="text-sm text-zinc-500">PDF, imagem ou Office. A Isa faz OCR, indexa e versiona automaticamente — fica pesquisável e citável, respeitando permissões.</p>
        <div className="mt-4 space-y-3">
          <label className="text-xs font-medium text-zinc-500">Visibilidade
            <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-zinc-900">
              {["department", "company", "private", "manager_only", "commercial_restricted"].map((v) => <option key={v} value={v}>{VIS_LABEL[v] || v}</option>)}
            </select>
          </label>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-zinc-200 py-8 text-sm text-zinc-500 transition-colors hover:border-indigo-300 hover:bg-indigo-50/40" data-testid="upload-knowledge">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />} {busy ? "Enviando..." : "Selecionar arquivo (PDF / imagem / Office)"}
            <input type="file" className="hidden" onChange={upload} accept=".pdf,image/*,.docx,.xlsx,.pptx" />
          </label>
        </div>
      </Card>

      <Card className="p-6 lg:col-span-2">
        <div className="mb-4 flex items-center gap-2"><Globe size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-zinc-900">Ler de um site (URL)</h2></div>
        <p className="text-sm text-zinc-500">Cole o link de uma página. A Isa lê o conteúdo, extrai o texto e guarda no cérebro — fica pesquisável e citável como qualquer conhecimento.</p>
        <form onSubmit={addUrl} className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-500">URL da página</label>
            <Input data-testid="knowledge-url" type="url" placeholder="https://..." value={urlF.url} onChange={(e) => setUrlF({ ...urlF, url: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-500">Título (opcional)</label>
            <Input data-testid="knowledge-url-title" placeholder="Deixe em branco p/ usar o título do site" value={urlF.title} onChange={(e) => setUrlF({ ...urlF, title: e.target.value })} />
          </div>
          <Btn data-testid="knowledge-url-submit" disabled={urlBusy} className="h-[42px] whitespace-nowrap">
            {urlBusy ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />} {urlBusy ? "Lendo..." : "Ler e salvar"}
          </Btn>
        </form>
        <select data-testid="knowledge-url-scope" value={urlF.scope} onChange={(e) => setUrlF({ ...urlF, scope: e.target.value })}
          className="mt-3 w-full rounded-md border border-input bg-white px-3 py-2 text-sm md:w-auto">
          <option value="company">Empresa</option>
          <option value="department:commercial">Departamento (Comercial)</option>
          <option value="team">Equipe</option>
        </select>
      </Card>
    </div>
  );
}

function Conhecimento() {
  const [nodes, setNodes] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/knowledge?status=${showAll ? "all" : "current"}`).then(({ data }) => setNodes(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showAll]);

  const del = async (n) => {
    if (!window.confirm(`Excluir "${n.title}" do cérebro? A Isa deixa de usar este conhecimento.`)) return;
    try {
      await api.delete(`/knowledge/${n.id}`);
      toast.success("Conhecimento removido do cérebro");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="space-y-4">
      <button onClick={() => setShowAll(!showAll)} data-testid="toggle-status" className="text-sm text-slate-500 hover:text-slate-800">
        {showAll ? "Ver só atuais" : "Ver arquivados também"}
      </button>
      <div className="grid gap-3 md:grid-cols-2">
        {nodes.map((n) => {
          const channel = CHANNEL_LABEL[n.source_channel];
          return (
            <Card key={n.id} className="group flex flex-col p-4 hover:shadow-md" data-testid={`knode-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-slate-900">{n.title}</p>
                <span className="flex shrink-0 items-center gap-1.5">
                  <Badge>v{n.version}</Badge>
                  <Badge tone={n.status === "current" ? "pos" : "muted"}>{KN_STATUS[n.status] || n.status}</Badge>
                </span>
              </div>
              <p className="mt-2 line-clamp-3 flex-1 text-sm text-slate-600">{n.content}</p>

              <div className="mt-3 flex items-end justify-between gap-2 border-t border-slate-100 pt-2.5">
                <div className="min-w-0 space-y-0.5 text-xs text-slate-400">
                  <p className="flex items-center gap-1 text-slate-500" data-testid={`knode-origin-${n.id}`}>
                    <User size={11} /> por <span className="font-medium text-slate-600">{n.author_name || "—"}</span>
                    {channel && <span className="text-slate-400">, {channel}</span>}
                  </p>
                  <p className="flex items-center gap-1">
                    <Clock size={11} /> {fmtWhen(n.created_at)} · {SCOPE_LABEL(n.scope)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => setEditing(n)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600" data-testid={`knode-edit-${n.id}`} title="Editar"><Pencil size={14} /></button>
                  <button onClick={() => del(n)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={`knode-del-${n.id}`} title="Excluir"><Trash2 size={14} /></button>
                </div>
              </div>
            </Card>
          );
        })}
        {!nodes.length && <p className="text-sm text-slate-500">Sem registros</p>}
      </div>

      {editing && <EditNodeModal node={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Comportamento() {
  const [replyMode, setReplyMode] = useState("auto");
  const [savingMode, setSavingMode] = useState(false);
  const [bh, setBh] = useState({ start: 8, end: 20, enabled: true });
  const [savingBh, setSavingBh] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/zapi/config");
      setReplyMode(data?.isa_reply_mode || "auto");
      if (data?.business_hours) setBh(data.business_hours);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveReplyMode = async (mode) => {
    setReplyMode(mode); setSavingMode(true);
    try { await api.post("/admin/zapi/config", { isa_reply_mode: mode }); toast.success("Preferência de resposta da ISA salva"); }
    catch (e) { toast.error(apiErr(e)); load(); } finally { setSavingMode(false); }
  };
  const saveBh = async (next) => {
    if (next.start >= next.end) return toast.error("O início do horário deve ser antes do fim.");
    setBh(next); setSavingBh(true);
    try {
      await api.post("/admin/zapi/config", {
        business_hours_start: next.start, business_hours_end: next.end, business_hours_enabled: next.enabled,
      });
      toast.success("Horário comercial salvo");
    } catch (e) { toast.error(apiErr(e)); load(); } finally { setSavingBh(false); }
  };

  if (loading) return <Card className="flex items-center justify-center p-12"><Loader2 className="animate-spin text-zinc-400" /></Card>;

  return (
    <div className="space-y-5">
      {/* Como a ISA responde */}
      <Card className="p-5" data-testid="isa-reply-mode-card">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Volume2 size={17} className="text-primary" /> Como a ISA responde</h3>
        <p className="mt-1 text-sm text-muted-foreground">Escolha se a ISA devolve o pitch e os materiais gerados em <b>texto</b> ou em <b>áudio</b> (voz) no WhatsApp.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[
            { key: "text", icon: Type, title: "Sempre texto", desc: "Responde sempre por escrito." },
            { key: "voice", icon: Volume2, title: "Sempre em áudio", desc: "Envia o pitch/material como voz." },
            { key: "auto", icon: Wand2, title: "Automático", desc: "Espelha: mandou áudio, responde em áudio." },
          ].map((o) => {
            const active = replyMode === o.key;
            const Icon = o.icon;
            return (
              <button key={o.key} type="button" disabled={savingMode}
                onClick={() => saveReplyMode(o.key)} data-testid={`reply-mode-${o.key}`}
                className={`flex flex-col items-start gap-1.5 rounded-xl border p-4 text-left transition-all ${active ? "border-primary bg-primary/5 ring-4 ring-primary/10" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                <span className="flex items-center gap-2 font-medium text-slate-900">
                  <Icon size={16} className={active ? "text-primary" : "text-slate-400"} /> {o.title}
                  {active && <CheckCircle2 size={14} className="text-primary" />}
                </span>
                <span className="text-xs text-muted-foreground">{o.desc}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">O áudio usa a voz da ISA (TTS) e vale para os materiais que ela gera (pitch, argumentário, roteiro, e-mail, resumo).</p>
      </Card>

      {/* Horário comercial da ISA */}
      <Card className="p-5" data-testid="business-hours-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Clock size={17} className="text-primary" /> Horário comercial da ISA</h3>
            <p className="mt-1 text-sm text-muted-foreground">Defina a janela em que a ISA pode enviar mensagens <b>proativas</b> (lembretes de reunião e o follow-up "como foi a reunião?"). Fora desse horário, ela aguarda e envia no próximo horário útil — nada de mensagem de madrugada. Horário de Brasília (BRT).</p>
          </div>
          <button type="button" disabled={savingBh}
            onClick={() => saveBh({ ...bh, enabled: !bh.enabled })}
            data-testid="business-hours-toggle"
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${bh.enabled ? "bg-primary" : "bg-slate-300"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${bh.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className={`mt-4 grid gap-4 sm:grid-cols-2 sm:max-w-md ${bh.enabled ? "" : "pointer-events-none opacity-40"}`}>
          <div>
            <label className="text-xs font-medium text-foreground">Início do expediente</label>
            <select data-testid="business-hours-start" disabled={!bh.enabled || savingBh}
              value={bh.start} onChange={(e) => saveBh({ ...bh, start: Number(e.target.value) })}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none ring-primary/20 focus:ring-4">
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">Fim do expediente</label>
            <select data-testid="business-hours-end" disabled={!bh.enabled || savingBh}
              value={bh.end} onChange={(e) => saveBh({ ...bh, end: Number(e.target.value) })}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none ring-primary/20 focus:ring-4">
              {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Moon size={12} />
          {bh.enabled
            ? `Silêncio ativo: nada é enviado antes das ${String(bh.start).padStart(2, "0")}:00 nem depois das ${String(bh.end).padStart(2, "0")}:00.`
            : "Silêncio desativado: a ISA pode enviar a qualquer hora (não recomendado)."}
        </p>
      </Card>
    </div>
  );
}

export default function Brain() {
  const { user } = useAuth();
  const isAdmin = ["admin", "owner", "superadmin"].includes(user?.role);
  const [tab, setTab] = useState("alimentar");
  const [refreshKey, setRefreshKey] = useState(0);
  const tabs = [
    { key: "alimentar", label: "Alimentar", icon: PenLine },
    { key: "conhecimento", label: "Conhecimento", icon: Library },
    { key: "documentos", label: "Documentos", icon: FileText },
    ...(isAdmin ? [{ key: "comportamento", label: "Comportamento da ISA", icon: SlidersHorizontal }] : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Cérebro</h1>
        <p className="mt-1 text-sm text-zinc-500">A memória da empresa: alimente escrevendo ou enviando documentos, e consulte o que já foi aprendido.</p>
      </div>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "alimentar" && <Alimentar onSaved={() => setRefreshKey((k) => k + 1)} />}
      {tab === "conhecimento" && <Conhecimento key={refreshKey} />}
      {tab === "documentos" && <Documents embedded />}
      {tab === "comportamento" && isAdmin && <Comportamento />}
    </div>
  );
}
